<template>
    <div>
        <h4>
            <strong>
                TOTAL:
            </strong>
            {{ formatNumber(totalCosts) }}
        </h4>
        <div class="panel">
            <b-table
                :data="costs"
                :loading="isLoadingTable"
                paginated
                backend-pagination
                :per-page="perPage"
                :total="total"
                @page-change="onPageChange"
                backend-sorting
                :default-sort="orderField"
                :default-sort-direction="orderDir"
                @sort="onSort"
                class="w-full"
                striped
                bordered
                icon-pack="fas"
                detailed
                detail-key="id"
                :show-detail-icon="showDetailIcon"
                scrollable
            >
                <template>
                    <b-table-column field="code" :label="$t('code')" sortable v-slot="props">
                        {{ props.row.code }}
                    </b-table-column>
                    <b-table-column field="name" :label="$t(type === 0 ? 'indirect_cost_name' : 'direct_cost_name')" sortable v-slot="props">
                        {{ props.row.name }}
                    </b-table-column>
                    <b-table-column field="price" :label="$t('amount')" sortable v-slot="props">
                        {{ formatNumber(props.row.price) }}
                    </b-table-column>
                    <b-table-column :label="$t('actions')" field="actions" centered width="150" v-slot="props">
                        <div class="w-full h-full flex items-center justify-around">
                            <b-tooltip :label="$t(type === 0 ? 'edit_indirect_cost' : 'edit_direct_cost')" position="is-left">
                                <div @click="editButtonClicked(props.row)" class="cursor-pointer flex text-blue">
                                    <b-icon pack="fas" icon="pencil-alt" />
                                </div>
                            </b-tooltip>
                            <b-tooltip
                            :label="$t(type === 0 ? 'edit_indirect_cost_detail' : 'edit_direct_cost_detail')"
                            >
                                <div @click="editDetailButtonClicked(props.row)" class="cursor-pointer flex text-blue">
                                    <b-icon pack="fas" icon="file-pen" />
                                </div>
                            </b-tooltip>
                            <b-tooltip :label="$t(type === 0 ? 'delete_indirect_cost' : 'remove')" position="is-left" type="is-danger">
                                <div @click="removeCost(props.row)" class="cursor-pointer flex text-red">
                                    <b-icon pack="fas" icon="trash-alt" />
                                </div>
                            </b-tooltip>
                        </div>
                    </b-table-column>
                </template>

                <!-- Detalles de Costos -->
                <template #detail="data">
                    <div v-if="data.row.cost_details && data.row.cost_details.length > 0">
                        <b-table
                            :data="data.row.cost_details"
                            striped
                            bordered
                            icon-pack="fas"
                            scrollable
                            detailed
                            detail-key="id"
                            :show-detail-icon="showDetailIcon"
                        >
                            <template>
                                <b-table-column field="code" :label="$t('code')" v-slot="props">
                                    {{ props.row.code }}
                                </b-table-column>
                                <b-table-column field="description" :label="$t('description')" v-slot="props">
                                    {{ props.row.description }}
                                </b-table-column>
                                <b-table-column field="price" :label="$t('price')" v-slot="props">
                                    {{ formatNumber(props.row.price) }}
                                </b-table-column>
                                <b-table-column field="quantity" :label="$t('quantity')" v-slot="props">
                                    {{ props.row.quantity }}
                                </b-table-column>
                                <b-table-column field="total" :label="$t('amount')" v-slot="props">
                                    {{ formatNumber(props.row.total) }}
                                </b-table-column>
                                <b-table-column field="actions" :label="$t('actions')" v-slot="props" centered>
                                    <div class="w-full h-full flex items-center justify-center">
                                        <b-tooltip :label="$t('add_invoice')" position="is-left">
                                            <div @click="openInvoiceCost(props.row)"
                                                class="cursor-pointer flex text-dark">
                                                <b-icon pack="fas" icon="circle-plus" />
                                            </div>
                                        </b-tooltip>
                                    </div>
                                </b-table-column>
                            </template>

                            <!-- Subdetalles: Facturas -->
                            <template #detail="invoice">
                                <div v-if="invoice.row.invoices && invoice.row.invoices.length > 0">
                                    <div class="flex justify-between">
                                        <h1 class="text-xl font-bold text-gray-800 mb-4">{{ $t('billing') }}</h1>
                                    </div>
                                    <table class="table">
                                        <thead class="bg-gray-200">
                                            <tr>
                                                <th>{{ $t('quantity') }}</th>
                                                <th>{{ $t('Import de coste real a Origen') }}</th>
                                                <th>{{ $t('actions') }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr class="bg-gray-200"
                                                v-for="(assignment, index) in invoice.row.invoices"
                                                :key="index">
                                                <td class="text-center"> {{ formatNumber(assignment.assignment) }}</td>
                                                <td class="text-center">{{ formatNumber(assignment.price) }}</td>
                                                <td class="text-center">
                                                    <b-tooltip :label="$t('edit_billing')" position="is-left">
                                                        <div @click="editButtonInvoice(invoice.row, assignment)"
                                                            class="cursor-pointer flex justify-center text-dark mx-4">
                                                            <b-icon pack="fas" icon="pencil-alt" />
                                                        </div>
                                                    </b-tooltip>
                                                    <b-tooltip :label="$t('delete_billing')" position="is-left"
                                                        type="is-danger">
                                                        <div @click="removeInvoice(assignment)"
                                                            class="cursor-pointer flex justify-center text-dark">
                                                            <b-icon pack="fas" icon="trash-alt" />
                                                        </div>
                                                    </b-tooltip>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-else>
                                    <p class="text-gray-800">{{ $t('no_more_results') }}</p>
                                </div>
                            </template>
                        </b-table>
                    </div>
                    <div v-else>
                        <p class="text-gray-800">{{ $t('no_more_results') }}</p>
                    </div>
                </template>

                <template #empty>
                    <div class="has-text-centered">{{ $t('no_results') }}</div>
                </template>
            </b-table>
        </div>
        <!-- Invoice Modal -->
        <InvoiceCostModal :projectId="projectId" ref="invoice_cost_modal" @reload-list="handleReload" />
    </div>
</template>

<script>
import axios from 'axios';
import InvoiceCostModal from './invoice/invoice-cost-modal.vue';

export default {
    props: {
        projectId: { type: Number, required: true },
        type: { type: Number, required: true }
    },
    components: {
        InvoiceCostModal,
    },
    computed: {
        filters() {
            return {
                order_field: this.orderField,
                order_dir: this.orderDir,
                perPage: this.perPage,
                page: this.page,
                project_id: this.projectId,
                type: this.type
            };
        },
    },
    created() {
        this.getCosts();
    },
    data() {
        return {
            isLoadingTable: true,
            perPage: 25,
            orderField: 'code',
            orderDir: 'asc',
            page: 1,
            total: 0,
            costs: [],
            totalCosts: null,
            showDetailIcon: true,
        };
    },
    methods: {
        async getCosts(filters = null) {
            this.isLoadingTable = true;
            const params = filters || this.filters;
            try {
                const { data } = await axios.get(`/api/v2/costs`, { params });
                if (data) {
                    this.costs = data.data;
                    this.total = data.meta.total;
                    this.totalCosts = data.meta.total_cost;
                }
            } catch (error) {
                console.error('Error fetching costs:', error);
            } finally {
                this.isLoadingTable = false;
            }
        },

        onSort(field, order) {
            this.orderField = field;
            this.orderDir = order;
            this.getCosts();
        },

        onPageChange(page) {
            this.page = page;
            this.getCosts();
        },

        reload(filters = {}) {
            const combinedFilters = { ...filters, ...this.filters };
            this.getCosts(combinedFilters);
        },

        async editButtonClicked(cost) {
            this.$emit('epc-edit', cost);
        },

        async removeCost(cost) {
            try {
                this.$dialog.confirm({
                    title: this.$t('delete'),
                    message: this.$t('delete_confirm_text', [cost.name]),
                    confirmText: this.$t('delete'),
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: async () => {
                        await axios.delete(`/api/v2/costs/${cost.id}`);
                        this.$buefy.toast.open({
                            message: this.$t('removed_successfully'),
                            type: 'is-success'
                        });
                        this.reload();
                    },
                });
            } catch (error) {
                this.$buefy.toast.open({
                    message: this.$t('remove_error') + (error.response?.data?.message || ''),
                    type: 'is-danger'
                });
            }
        },

        async removeInvoice(invoice) {
            try {
                this.$dialog.confirm({
                    title: this.$t('delete'),
                    message: this.$t('delete_billing_confirm_text', [invoice.id]),
                    confirmText: this.$t('delete'),
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: async () => {
                        await axios.delete(`/api/v2/invoice-costs/${invoice.id}`);
                        this.$buefy.toast.open({
                            message: this.$t("removed_successfully"),
                            type: 'is-success'
                        });
                        this.reload();
                    },
                });
            } catch (error) {
                this.$buefy.toast.open({
                    message: this.$t("remove_error") + (error.response?.data?.message || ''),
                    type: 'is-danger'
                });
            }
        },
        formatNumber(value) {
            const number = parseFloat(value);
            if (isNaN(number)) return '0,00';
            let [integerPart, decimalPart] = number.toFixed(2).split('.');
            integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            return `${integerPart},${decimalPart}`;
        },

        openInvoiceCost(cost) {
            this.$refs.invoice_cost_modal.show(cost);
        },
        handleReload() {
            this.reload();
        },
        async editButtonInvoice(cost, invoiceCost) {
            this.$refs.invoice_cost_modal.show(cost, invoiceCost);
        },
        editDetailButtonClicked(cost) {
            this.$router.push({
                name: 'project.admin.cost-management.cost-detail',
                params: { project_id: this.projectId, type: this.type, cost: cost },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
table {
    border-collapse: none;
}

table td,
.table th {
    border: 1px solid #E2E8F0;
    padding: 0.5em 0.75em;
    text-align: center;
}

.table th {
    background-color: #4A5568;
    color: #FFFFFF;
}
</style>